import { Collapse, IconButton, Typography } from '@material-ui/core';
import { AssignmentSharp } from '@material-ui/icons';
import MenuIcon from '@material-ui/icons/Menu';
import { useEffect, useState } from 'react';

import { Navigate } from 'react-router';
import { userProfileApi } from '../../api/user-profile-api';
import { auth, getStoredOrgUrlParams, requestActivity } from '../../core';
import {
  AuditBatchPage,
  AuditReviewPage,
  coderAuditColumns,
  openReadonlyVisit
} from '../../core/audit-batch';
import { auditBatch } from '../../core/audit-batch-api';
import CoderProductivity from '../../core/coder-productivity';
import { Visit } from '../../core/domain';
import { Spinner } from '../../core/loading';
import { OrganizationSelection } from '../../core/organization-api';
import { Page } from '../../core/page';
import { PagedTable, TableColumn } from '../../core/paged-table';
import { history } from '../../core/router';
import { setCurrentVisitSession } from '../../core/visit-context';
import { VisitSearchResult, worksheetQueue } from '../../core/visit-queue';
import {
  currentWorkflow,
  currentWorkflowPrompt,
  workflows
} from '../../core/workflow-api';
import { WorkflowStatus } from '../../core/workflows';
import { AuditSearch } from '../audit-list/audit-search';
import { OrganizationSearchHeader, PeriodSearch } from '../common/forms';
import { loadingPage } from '../common/presentation';
import { visitListApi, VisitListApi } from './visit-list-api';
import { VisitSearch } from './visit-search';

interface VisitPageProps extends VisitListApi {
  filterOpen?: boolean;
  hideFilter?: boolean;
  audit?: boolean;
  onLogout?: () => void;
  onVisitClick?: (a: Visit) => void;
}

export function VisitTable(props: VisitPageProps) {
  const { api, filterOpen, org, columns, pageApi, hideFilter, audit } = props;
  const { search, setQuery, updateOrAddSort, updateColumnSort } = api.actions;

  const onRequestPage = ({ pageNumber, pageSize }) => {
    setQuery({ pageNumber, pageSize });
    search();
  };

  const onRequestSort = (sortOrder: any, c: TableColumn) => {
    if (api.data.getState().advancedActive) {
      updateOrAddSort({ ...{ sortOrder }, ...c.data });
    } else {
      updateColumnSort({ ...{ sortOrder }, ...c.data });
    }
    search();
  };

  const onVisitClick = (v: Visit) => {
    if (v) {
      if (props.onVisitClick) {
        if (result?.entries) {
          worksheetQueue.setState({ visit: v });
        }
        props.onVisitClick(v);
      } else {
        setCurrentVisitSession(v);
        history.push('./tool');
      }
    }
  };

  const orgUpdate = (s: OrganizationSelection) => {
    if (!api.data.getState().pendingNavigation) {
      if (s && s.location && s.department) {
        setQuery({
          locationCode: s.location.code,
          departmentCode: s.department && s.department.code,
        });
        search();
      }
    }
  };

  useEffect(() => {
    orgUpdate(org.getState().selection);
    return org.subscribe(
      (s: OrganizationSelection) => {
        orgUpdate(s);
      },
      (s) => s.selection
    );
  }, []);

  const result: VisitSearchResult = worksheetQueue((s) => s.result);
  result && loadingPage.setState({ show: false });

  const totalResults = (result && result.totalResults) || 0;
  const selected = worksheetQueue((s) => s.visit);
  const rowIsSelected = (r: Visit) =>
    selected && selected.visitId === r.visitId;

  const codingContainerHeight = filterOpen ? '72vh' : '83vh';

  return (
    <div style={{ height: audit ? undefined : codingContainerHeight }}>
      {hideFilter ? null : (
        <Collapse in={filterOpen}>
          {audit ? <AuditSearch api={api} /> : <VisitSearch api={api} />}
        </Collapse>
      )}
      <PagedTable
        id="visits"
        containerHeight={codingContainerHeight}
        onRowClick={onVisitClick}
        rowIsSelected={rowIsSelected}
        columns={columns}
        rows={result.entries}
        totalResults={totalResults}
        pageApi={pageApi}
        onRequestPage={onRequestPage}
        onRequestSort={onRequestSort}
        navigationStyle={{
          width: '98vw',
          backgroundColor: 'white',
          position: 'absolute',
          bottom: 0,
        }}
      />
    </div>
  );
}

export function VisitPage(props: VisitPageProps) {
  const { org, api, onLogout } = props;

  const { search, setServiceDate } = api.actions;

  const onPeriodSearch = async (args: PeriodSearch) => {
    api.data.setState({ period: args });
    setServiceDate(args.period);
    search();
  };

  const loading = requestActivity((s) => s.active);

  const [open, setOpen] = useState(false);
  const toggleOpen = () => {
    setOpen(!open);
    api.data.setState({ advancedActive: !open });
  };

  const title = (
    <Typography style={{ paddingRight: 24 }} variant="h6" noWrap>
      {currentWorkflowPrompt()}
    </Typography>
  );

  const showCodingList = currentWorkflow() != 'auditStd';
  const tableProps = { ...props, ...{ columns: props?.getColumns() } };

  const availableWorkflows = workflows.data((s) => s?.workflows);
  return Page({
    loading: loading,
    mainHeader: (
      <OrganizationSearchHeader
        api={org}
        title={title}
        onLogout={onLogout}
        titleIcon={
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {
              <IconButton id="open-filters" onClick={toggleOpen} edge="start">
                {open ? <MenuIcon htmlColor="white" id="open_filter_icon" /> : <MenuIcon id="open_menu_icon"/>}
              </IconButton>
            }
            <CoderProductivity />
            {availableWorkflows?.length ? (
              <WorkflowStatus
                color="white"
                onClick={() => workflows.actions.workflowSelectorOpen(true)}
              />
            ) : (
              <IconButton disabled={true}>
                <AssignmentSharp htmlColor="white" />
              </IconButton>
            )}
          </div>
        }
        onSearch={onPeriodSearch}
      />
    ),
    main: showCodingList ? (
      <VisitTable {...tableProps} filterOpen={open} />
    ) : (
      <AuditBatchPage
        api={auditBatch.actions.setOrg(org)}
        selection={org.getState().selection}
        searchResults={
          <VisitTable {...tableProps} filterOpen={open} audit={true} />
        }
      />
    ),
  });
}

const isActivity = !!getStoredOrgUrlParams().activity;
const api = isActivity ? null : visitListApi();
export const codingVisitsApi = api;

export function VisitRoute({ signOut }) {
  const data = api.org((s) => s.organizations)?.length > 0;

  /// TODO: test
  const user = userProfileApi.data.getState().user;
  api.setUser(user);

  return (
    data && 
      <VisitPage {...api} onLogout={signOut} />
  );
}

export function VisitPageRoute({ signOut }) {
  const data = api.org((s) => s.pendingOrComplete);

  if (!auth.isAuthenticated() || !data) {
    if (!data) {
      api.org.getState().loadOrganizations(null);
    }
    return <Navigate replace to="/" />;
  }
  const reset = worksheetQueue.getState().reset;
  // console.log('loading visit page, checking for reset', reset);
  if (reset) {
    api.resetPage();
    worksheetQueue.setState({ reset: false });
  }

  return <VisitRoute signOut={signOut} />;
}

export function AuditReview(props: VisitPageProps) {
  const { org, onLogout } = props;
  const loading = requestActivity((s) => s.active);
  const title = (
    <Typography style={{ paddingRight: 24 }} variant="h6" noWrap>
      {currentWorkflowPrompt()}
    </Typography>
  );
  const tableProps = {
    ...props,
    ...{ columns: coderAuditColumns, onVisitClick: openReadonlyVisit },
  };

  return Page({
    loading: loading,
    mainHeader: (
      <OrganizationSearchHeader
        api={org}
        title={title}
        onLogout={onLogout}
        titleIcon={
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <CoderProductivity />
            <WorkflowStatus
              color="white"
              onClick={() => workflows.actions.workflowSelectorOpen(true)}
            />
          </div>
        }
        onSearch={() => null}
      />
    ),
    main: (
      <AuditReviewPage
        api={auditBatch.actions.setOrg(org)}
        selection={org.getState().selection}
        searchResults={
          <VisitTable {...tableProps} hideFilter={true} audit={true} />
        }
      />
    ),
  });
}

export function AuditReviewPageRoute({ signOut }) {
  const data = api.org((s) => s.pendingOrComplete);

  if (!auth.isAuthenticated() || !data) {
    if (!data) {
      api.org.getState().loadOrganizations(null);
    }
    return <Navigate replace to="/" />;
  }

  auditBatch.actions.auditReview();

  return (
    <Spinner complete={data}>
      <AuditReview {...api} onLogout={signOut} />
    </Spinner>
  );
}
